import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@/convex/src/_generated/api";
import { useAppWorkspace } from "@/contexts/AppWorkspaceContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MoreHorizontal, Mail, Copy } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format } from "date-fns";

/**
 * MembersSection
 *
 * Member management interface
 * Shows for admin+ roles
 */
export function MembersSection() {
  const { activeWorkspace, userRole } = useAppWorkspace();

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member" | "viewer">(
    "member",
  );
  const [isInviting, setIsInviting] = useState(false);

  // Queries
  const members = useQuery(
    api.workspaceMembers.listMembers,
    activeWorkspace ? { workspaceId: activeWorkspace._id } : "skip",
  );

  const invitations = useQuery(
    api.workspaceMembers.listInvitations,
    activeWorkspace ? { workspaceId: activeWorkspace._id } : "skip",
  );

  // Mutations
  const inviteMemberMutation = useMutation(api.workspaceMembers.inviteMember);
  const updateMemberRoleMutation = useMutation(
    api.workspaceMembers.updateMemberRole,
  );
  const removeMemberMutation = useMutation(api.workspaceMembers.removeMember);
  const leaveMutation = useMutation(api.workspaceMembers.leave);
  const cancelInvitationMutation = useMutation(
    api.workspaceMembers.cancelInvitation,
  );

  if (!activeWorkspace) {
    return <div>No workspace selected</div>;
  }

  const canManageMembers = userRole === "owner" || userRole === "admin";

  const handleInvite = async () => {
    if (!canManageMembers) return;
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    setIsInviting(true);
    try {
      const result = await inviteMemberMutation({
        workspaceId: activeWorkspace._id,
        email: inviteEmail.trim(),
        role: inviteRole,
      });

      // Copy invitation link to clipboard
      const inviteLink = `${window.location.origin}?invite=${result.token}`;
      await navigator.clipboard.writeText(inviteLink);

      toast.success("Invitation created and link copied to clipboard");
      setInviteEmail("");
    } catch (error: any) {
      console.error("Failed to invite member:", error);
      toast.error(error.message || "Failed to invite member");
    } finally {
      setIsInviting(false);
    }
  };

  const handleChangeRole = async (
    memberId: any,
    newRole: "admin" | "member" | "viewer",
  ) => {
    if (!canManageMembers) return;

    try {
      await updateMemberRoleMutation({
        workspaceId: activeWorkspace._id,
        memberId,
        newRole,
      });
      toast.success("Member role updated");
    } catch (error: any) {
      console.error("Failed to update role:", error);
      toast.error(error.message || "Failed to update role");
    }
  };

  const handleRemove = async (memberId: any) => {
    if (!canManageMembers) return;

    try {
      await removeMemberMutation({
        workspaceId: activeWorkspace._id,
        memberId,
      });
      toast.success("Member removed");
    } catch (error: any) {
      console.error("Failed to remove member:", error);
      toast.error(error.message || "Failed to remove member");
    }
  };

  const handleLeave = async () => {
    try {
      await leaveMutation({ workspaceId: activeWorkspace._id });
      toast.success("Left workspace");
    } catch (error: any) {
      console.error("Failed to leave workspace:", error);
      toast.error(error.message || "Failed to leave workspace");
    }
  };

  const handleCopyInviteLink = async (token: string) => {
    const inviteLink = `${window.location.origin}?invite=${token}`;
    await navigator.clipboard.writeText(inviteLink);
    toast.success("Invitation link copied to clipboard");
  };

  const handleCancelInvitation = async (invitationId: any) => {
    try {
      await cancelInvitationMutation({
        workspaceId: activeWorkspace._id,
        invitationId,
      });
      toast.success("Invitation cancelled");
    } catch (error: any) {
      console.error("Failed to cancel invitation:", error);
      toast.error(error.message || "Failed to cancel invitation");
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold mb-6">Members</h1>

      {!canManageMembers && (
        <div className="bg-muted/30 border border-border rounded-lg p-4 mb-6 max-w-2xl">
          <p className="text-sm text-muted-foreground">
            You don't have permission to manage members. Contact an admin or
            owner to invite or remove team members.
          </p>
        </div>
      )}

      {canManageMembers && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Invite Member</h3>
          <div className="flex gap-2 max-w-2xl">
            <div className="flex-1">
              <Input
                placeholder="email@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleInvite();
                  }
                }}
              />
            </div>
            <Select
              value={inviteRole}
              onValueChange={(value: any) => setInviteRole(value)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleInvite} disabled={isInviting}>
              {isInviting ? "Inviting..." : "Invite"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            An invitation link will be copied to your clipboard
          </p>
        </div>
      )}

      <div>
        <h3 className="text-lg font-semibold mb-4">Members</h3>
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                {canManageMembers && (
                  <TableHead className="w-[50px]"></TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {members?.map((member) => (
                <TableRow key={member._id}>
                  <TableCell>{member.name || "—"}</TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="capitalize">
                      {member.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {format(new Date(member.joinedAt), "MMM d, yyyy")}
                  </TableCell>
                  {canManageMembers && member.role !== "owner" && (
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {(userRole === "owner" ||
                            (userRole === "admin" &&
                              member.role !== "admin")) && (
                            <>
                              <DropdownMenuItem
                                onClick={() =>
                                  handleChangeRole(member._id, "admin")
                                }
                              >
                                Make Admin
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  handleChangeRole(member._id, "member")
                                }
                              >
                                Make Member
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  handleChangeRole(member._id, "viewer")
                                }
                              >
                                Make Viewer
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleRemove(member._id)}
                            className="text-destructive"
                          >
                            Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {canManageMembers && invitations && invitations.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Pending Invitations</h3>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Invited By</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invitations.map((invitation) => (
                  <TableRow key={invitation._id}>
                    <TableCell>{invitation.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {invitation.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {invitation.inviterName || invitation.inviterEmail}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(invitation.expiresAt), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyInviteLink(invitation.token)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelInvitation(invitation._id)}
                          className="text-destructive"
                        >
                          Cancel
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {userRole !== "owner" && (
        <div className="border-t pt-6">
          <h4 className="text-base font-semibold mb-2">Leave Workspace</h4>
          <p className="text-sm text-muted-foreground mb-4">
            You will no longer have access to this workspace.
          </p>
          <Button variant="outline" onClick={handleLeave}>
            Leave Workspace
          </Button>
        </div>
      )}
    </div>
  );
}
